/** @file Undoes the PNG scanline filters (None, Sub, Up, Average, Paeth) of one pass in place. */

/** Undoes one filter on the row at `row` (`n` bytes) against the unfiltered row at `prior` (-1 on the first row). */
type Unfilter = (d: Uint8Array, row: number, prior: number, n: number, bpp: number) => void;

const at = (d: Uint8Array, i: number): number => d[i] ?? 0;
/** The byte one pixel left (0 before the first pixel). */
const left = (d: Uint8Array, row: number, i: number, bpp: number): number => (i < bpp ? 0 : at(d, row + i - bpp));
/** The byte above (0 on the first row). */
const up = (d: Uint8Array, prior: number, i: number): number => (prior < 0 || i < 0 ? 0 : at(d, prior + i));

/** Paeth predictor: whichever of left, up and up-left is nearest to left + up - up-left. */
function paeth(a: number, b: number, c: number): number {
  const pa = Math.abs(b - c);
  const pb = Math.abs(a - c);
  const pc = Math.abs(a + b - 2 * c);
  return pa <= pb && pa <= pc ? a : pb <= pc ? b : c;
}

/** Filter types 0-4 (a Uint8Array store wraps each sum mod 256). */
const FILTERS: readonly Unfilter[] = [
  () => undefined,
  (d, row, _prior, n, bpp) => {
    for (let i = bpp; i < n; i++) d[row + i] = at(d, row + i) + at(d, row + i - bpp);
  },
  (d, row, prior, n) => {
    for (let i = 0; i < n && prior >= 0; i++) d[row + i] = at(d, row + i) + at(d, prior + i);
  },
  (d, row, prior, n, bpp) => {
    for (let i = 0; i < n; i++) d[row + i] = at(d, row + i) + ((left(d, row, i, bpp) + up(d, prior, i)) >> 1);
  },
  (d, row, prior, n, bpp) => {
    for (let i = 0; i < n; i++) d[row + i] = at(d, row + i) + paeth(left(d, row, i, bpp), up(d, prior, i), up(d, prior, i - bpp));
  },
];

/**
 * Unfilters `rows` scanlines starting at `start` (each a filter byte then `rowBytes` bytes) in place; `bpp` is bytes
 * per complete pixel (1 for sub-byte depths). Returns the first unknown filter type, or -1 when every row is fine.
 */
export function unfilterScanlines(data: Uint8Array, start: number, rows: number, rowBytes: number, bpp: number): number {
  const stride = rowBytes + 1;
  for (let y = 0; y < rows; y++) {
    const row = start + y * stride + 1;
    const type = at(data, row - 1);
    const unfilter = FILTERS[type];
    if (unfilter === undefined) return type;
    unfilter(data, row, y === 0 ? -1 : row - stride, rowBytes, bpp);
  }
  return -1;
}
